import React, { useState } from "react";
import { Users } from "lucide-react";
import modelsData from "../mock/models.json";

export const modelsFor = (slug) => {
  const all = modelsData[slug] || [];
  return [...all.filter((m) => m.img), ...all.filter((m) => !m.img)];
};

export const modelsWithPhotos = (slug) => (modelsData[slug] || []).filter((m) => m.img);

const initials = (name) => name.trim().charAt(0).toUpperCase();

const Avatar = ({ m, theme, size = "h-14 w-14" }) => (
  <div className={`${size} overflow-hidden rounded-full border ${theme.rule} bg-white`}>
    {m.img ? (
      <img src={m.img} alt={m.name} loading="lazy" className="h-full w-full object-cover" />
    ) : (
      <span className={`flex h-full w-full items-center justify-center font-serif ${theme.accent}`}>
        {initials(m.name)}
      </span>
    )}
  </div>
);

/** Compact 3-row strip of model icons used inside the venue overview cards. */
export const ModelStrip = ({ venue, perRow = 5, rows = 3 }) => {
  const all = modelsWithPhotos(venue.slug);
  if (!all.length) return null;
  const shown = all.slice(0, perRow * rows);
  const remaining = modelsFor(venue.slug).length - shown.length;
  const total = modelsFor(venue.slug).length;
  const t = venue.theme;

  const jump = () => {
    const el = document.getElementById(`${venue.slug}-models`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mt-6 border-t border-black/10 pt-5" data-testid={`model-strip-${venue.slug}`}>
      <div className="flex items-center justify-between">
        <p className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] ${t.accent}`}>
          <Users className="h-3.5 w-3.5" /> The Ladies
        </p>
        <p className="text-sm font-semibold text-foreground" data-testid={`model-count-${venue.slug}`}>
          {total} in total
        </p>
      </div>

      <div className="mt-4 grid grid-cols-6 gap-x-3 gap-y-4">
        {shown.map((m, i) => (
          <div key={`${m.name}-${i}`} className="flex flex-col items-center text-center">
            <Avatar m={m} theme={t} />
            <span className="mt-1.5 block w-full truncate text-[0.7rem] font-medium text-foreground">
              {m.name}
            </span>
            <span className="block text-[0.62rem] text-muted-foreground">
              {m.age ? `${m.age} yrs` : m.nationality || ""}
            </span>
          </div>
        ))}
      </div>

      {remaining > 0 && (
        <button
          onClick={jump}
          data-testid={`model-more-${venue.slug}`}
          className={`mt-4 w-full rounded-md py-2 text-sm font-semibold ${t.chip}`}
        >
          {remaining}+ more ladies, view all {total}
        </button>
      )}
    </div>
  );
};

/** Full roster for a venue, with age / hair / nationality filters. */
const AGE_BANDS = [
  { id: "18-24", label: "18-24", test: (a) => a >= 18 && a <= 24 },
  { id: "25-29", label: "25-29", test: (a) => a >= 25 && a <= 29 },
  { id: "30-34", label: "30-34", test: (a) => a >= 30 && a <= 34 },
  { id: "35+", label: "35+", test: (a) => a >= 35 },
];

export const ModelGrid = ({ venue }) => {
  const all = modelsFor(venue.slug);
  const t = venue.theme;
  const [age, setAge] = useState("all");
  const [hair, setHair] = useState("all");
  const [nat, setNat] = useState("all");

  if (!all.length) return null;

  const hasAges = all.some((m) => m.age);
  const hairs = [...new Set(all.map((m) => m.hair).filter(Boolean))].sort();
  const nats = [...new Set(all.map((m) => m.nationality).filter(Boolean))].sort();

  const band = AGE_BANDS.find((b) => b.id === age);
  const shown = all.filter(
    (m) =>
      (!band || (m.age && band.test(Number(m.age)))) &&
      (hair === "all" || m.hair === hair) &&
      (nat === "all" || m.nationality === nat)
  );

  const filtering = age !== "all" || hair !== "all" || nat !== "all";
  const reset = () => {
    setAge("all");
    setHair("all");
    setNat("all");
  };

  const pill = (isActive) =>
    `rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
      isActive ? `${t.chip} border-transparent` : `border-border bg-background text-muted-foreground hover:text-foreground`
    }`;

  return (
    <div className="mt-9" data-testid={`model-grid-${venue.slug}`}>
      <p className="mb-6 text-base text-muted-foreground">
        <span className="font-semibold text-foreground">{all.length} ladies</span> currently rent
        rooms at {venue.name}.
      </p>

      {/* filters */}
      <div
        data-testid={`model-filters-${venue.slug}`}
        className={`rounded-xl border p-5 ${t.rule} bg-background/70`}
      >
        <div className="flex flex-wrap items-start gap-x-10 gap-y-5">
          {hasAges && (
            <div>
              <p className="mb-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Age
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid={`filter-age-all-${venue.slug}`}
                  onClick={() => setAge("all")}
                  className={pill(age === "all")}
                >
                  Any age
                </button>
                {AGE_BANDS.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`filter-age-${b.id}-${venue.slug}`}
                    onClick={() => setAge(b.id)}
                    className={pill(age === b.id)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Hair
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid={`filter-hair-all-${venue.slug}`}
                onClick={() => setHair("all")}
                className={pill(hair === "all")}
              >
                Any hair
              </button>
              {hairs.map((h) => (
                <button
                  key={h}
                  data-testid={`filter-hair-${venue.slug}`}
                  onClick={() => setHair(h)}
                  className={pill(hair === h)}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Nationality
            </p>
            <select
              data-testid={`filter-nationality-${venue.slug}`}
              value={nat}
              onChange={(e) => setNat(e.target.value)}
              className="rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium text-foreground outline-none"
            >
              <option value="all">All nationalities</option>
              {nats.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4">
          <p className="text-sm text-muted-foreground" data-testid={`model-shown-${venue.slug}`}>
            Showing <span className="font-semibold text-foreground">{shown.length}</span> of{" "}
            {all.length} ladies
          </p>
          {filtering && (
            <button
              data-testid={`filter-reset-${venue.slug}`}
              onClick={reset}
              className={`text-sm font-semibold ${t.accent}`}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {shown.length === 0 ? (
        <p
          data-testid={`model-empty-${venue.slug}`}
          className="mt-8 rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground"
        >
          No ladies match those filters right now. Try clearing one of them.
        </p>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
          {shown.map((m, i) => (
            <figure
              key={`${m.name}-${i}`}
              data-testid={`model-card-${venue.slug}`}
              className={`overflow-hidden rounded-xl border bg-background/80 ${t.rule}`}
            >
              <div className="aspect-[3/4] w-full overflow-hidden bg-black/5">
                {m.img ? (
                  <img
                    src={m.img}
                    alt={m.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                ) : (
                  <span
                    className={`flex h-full w-full items-center justify-center font-serif text-3xl ${t.accent}`}
                  >
                    {initials(m.name)}
                  </span>
                )}
              </div>
              <figcaption className="px-3 py-3">
                <span className="block font-serif text-base leading-tight text-foreground">
                  {m.name}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {[m.age ? `Age ${m.age}` : null, m.hair, m.nationality]
                    .filter(Boolean)
                    .join(" \u00b7 ")}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
};
